import { useEffect, useRef, useState } from 'react'
import { Button, Modal } from 'antd'
import { Link } from 'react-router-dom'
import { API } from 'src/utils/interceptor'
import { getVenueRoleDisplayName } from 'src/utils/venueRoleCapabilities'
import {
  bulkActionConfirmation,
  menuAlreadyLines,
  menuConfirmBlocked,
  menuDishesCannotLand,
  menuSomeDishesCannotLand,
  alreadyHaveLines,
  missingRecipeLines,
  allergenDetailLines,
  allergenEditedLine,
  allergenNothingWillCopy,
  revenueNothingWillCopy,
  showCopyWithoutDishes,
  showMenuRecipeCounts,
  showOnlyMissingChoice,
  onlyMissingLocked,
  shoppingListNeedsSupplier,
  restrictedSuppliersCannotLand,
  restrictedApproverGaps,
  restrictedApproversBlockConfirm,
  shoppingListSupplierSummary,
  shoppingListSupplierVenueGroups,
  shoppingListSuppliersToEnable,
  recipeGapVenueLinks,
} from 'src/screens/venue-groups/bulkActionConfirmation'
import {
  ingredientGapLines,
  menuRecipeCountLines,
} from 'src/screens/venue-groups/runReceipt'
import { startAndPollVenueGroupRun } from 'src/screens/venue-groups/startAndPollVenueGroupRun'
import {
  RejectedNotification,
  SuccessNotification,
} from 'src/notifications/SuccessNotification'

/**
 * One confirmation for every bulk action. It does not start the run.
 * onConfirm is called only after the user accepts the real counts.
 */
const BulkActionConfirm = ({
  open,
  groupId,
  action,
  payload,
  onCancel,
  onConfirm,
  confirming,
  sourceTitle,
}) => {
  const [preview, setPreview] = useState(null)
  const [replaceIds, setReplaceIds] = useState([])
  const [replaceAll, setReplaceAll] = useState(false)
  const [onlyMissing, setOnlyMissing] = useState(false)
  const [forceShare, setForceShare] = useState(false)
  const [offerForceShare, setOfferForceShare] = useState(false)
  const [allowWithoutDishes, setAllowWithoutDishes] = useState(false)
  const [allowPartialList, setAllowPartialList] = useState(false)
  const [allowWithoutApprover, setAllowWithoutApprover] = useState(false)
  const [enablingSuppliers, setEnablingSuppliers] = useState(false)
  const [previewNonce, setPreviewNonce] = useState(0)

  const payloadKey = JSON.stringify(payload || {})
  const onCancelRef = useRef(onCancel)
  onCancelRef.current = onCancel

  /** Esc / Back: drop Review ticks so the next open is clean. */
  const clearReviewSelections = () => {
    setReplaceIds([])
    setReplaceAll(false)
    setOnlyMissing(false)
    setForceShare(false)
    setOfferForceShare(false)
    setAllowWithoutDishes(false)
    setAllowPartialList(false)
    setAllowWithoutApprover(false)
    setEnablingSuppliers(false)
    setPreview(null)
    setPreviewNonce(0)
  }

  const dismissReview = () => {
    clearReviewSelections()
    onCancelRef.current?.()
  }

  useEffect(() => {
    if (!open) return undefined
    clearReviewSelections()
    return undefined
  }, [open, groupId, action, payloadKey])

  useEffect(() => {
    if (!open || !groupId || !action) return undefined
    let cancelled = false
    setPreview(null)
    setReplaceIds([])
    setReplaceAll(false)
    const previewPayload = {
      ...(payload || {}),
      ...(forceShare ? { forceShare: true } : {}),
      ...(onlyMissing ? { onlyMissing: true } : {}),
    }
    API.post('api2', `venue-groups/${groupId}/runs/preview`, {
      body: { action, payload: previewPayload },
    })
      .then(response => {
        if (cancelled) return null
        const data = response?.data || null
        setPreview(data)
        const missing =
          (data?.recipeSkips || 0) > 0 ||
          (data?.ingredientGaps || []).length > 0 ||
          (data?.reasons || []).some(row =>
            /missing ingredients/i.test(row.reason || ''),
          )
        if (missing) setOfferForceShare(true)
        return null
      })
      .catch(error => {
        if (cancelled) return null
        RejectedNotification({
          message:
            error?.response?.data?.message || 'Could not check this group.',
        })
        dismissReview()
        return null
      })
    return () => {
      cancelled = true
    }
  }, [open, groupId, action, payloadKey, forceShare, onlyMissing, previewNonce])

  const enableSuppliersOnTargets = async () => {
    const sourceVenueId = payload?.sourceVenueId
    const toEnable = shoppingListSuppliersToEnable(
      preview?.shoppingListSuppliers,
    )
    if (!sourceVenueId || !toEnable.length) return
    setEnablingSuppliers(true)
    try {
      for (const row of toEnable) {
        await startAndPollVenueGroupRun({
          groupId,
          action: 'connect_supplier',
          payload: { sourceVenueId, supplierId: row.supplierId },
        })
      }
      SuccessNotification({
        message: 'Suppliers turned on where they were missing.',
      })
      setPreviewNonce(count => count + 1)
    } catch (error) {
      RejectedNotification({
        message:
          error?.response?.data?.message ||
          'Could not turn those suppliers on.',
      })
    } finally {
      setEnablingSuppliers(false)
    }
  }

  const willChange = preview?.willChange || 0
  const noDishes =
    preview?.action === 'copy_menu' &&
    menuDishesCannotLand(preview?.recipeCounts)
  const partialDishes =
    preview?.action === 'copy_menu' &&
    menuSomeDishesCannotLand(preview?.recipeCounts)
  const revenueAlreadyThere = preview?.revenuePreview?.alreadyThere || []
  const lockOnlyMissing = onlyMissingLocked({
    action: preview?.action,
    revenueAlreadyThere,
  })
  const onlyMissingChecked = lockOnlyMissing || onlyMissing
  const checkboxStyle = { marginRight: 8 }
  const revenueBlocked =
    preview?.action === 'assign_revenue_categories' &&
    revenueNothingWillCopy(preview?.revenuePreview)
  const removing = action === 'change_venue_person' && payload?.remove === true
  return (
    <Modal
      visible={open}
      destroyOnClose
      keyboard
      maskClosable
      title={removing ? 'Confirm this removal' : 'Confirm this change'}
      okText='Confirm'
      cancelText='Back'
      confirmLoading={confirming}
      okButtonProps={{
        disabled:
          !preview ||
          willChange === 0 ||
          menuConfirmBlocked({
            noDishes,
            partial: partialDishes,
            allowWithoutDishes,
          }) ||
          allergenNothingWillCopy({
            action: preview?.action,
            rows: preview?.allergenPreview,
          }) ||
          revenueBlocked ||
          restrictedSuppliersCannotLand(preview?.restrictedPreview) ||
          restrictedApproversBlockConfirm({
            approvers: payload?.approvers,
            allowWithoutApprover,
          }) ||
          (shoppingListNeedsSupplier(preview?.shoppingListSuppliers) &&
            !allowPartialList),
      }}
      onOk={() =>
        onConfirm({
          replaceVenueIds: replaceAll ? [] : replaceIds,
          replaceAllLocalChanges: replaceAll,
          onlyMissing: onlyMissingChecked,
          forceShare,
        })
      }
      onCancel={dismissReview}
    >
      {preview ? (
        <>
          <p>
            {bulkActionConfirmation({
              action: preview.action,
              groupName: preview.groupName,
              willChange: preview.willChange,
              skipped: preview.skipped,
              reasons: preview.reasons,
              excludesSource: preview.excludesSource === true,
              recipeSkips: preview.recipeSkips || 0,
              recipeCounts: preview.recipeCounts || [],
              ingredientGaps: preview.ingredientGaps || [],
              menuName: preview.menuName || '',
              emptyShare: preview.emptyShare === true,
              allowWithoutDishes,
              allergenPreview: preview.allergenPreview || [],
              sourceTitle,
              revenuePreview: preview.revenuePreview,
              restrictedPreview: preview.restrictedPreview,
              fromTitle: payload?.fromProductTitle,
              toTitle: payload?.toProductTitle,
              sharePeople: payload?.people,
              shareVenueTitles: payload?.shareVenueTitles,
              inviteEmail: payload?.inviteEmail
                ? {
                    ...payload.inviteEmail,
                    ...(preview.invitePreview
                      ? {
                          knownAccount:
                            preview.invitePreview.knownAccount === true,
                          venueTitles: preview.invitePreview.willAdd || [],
                          leftVenues: preview.invitePreview.left || [],
                        }
                      : {}),
                  }
                : payload?.inviteEmail,
              removePerson: payload?.removePerson
                ? {
                    ...payload.removePerson,
                    ...(payload.remove === true && preview.rolePreview
                      ? {
                          venueTitles: (
                            preview.rolePreview.willChange || []
                          ).map(row => ({
                            title: row.title,
                            currentRole: row.currentRole
                              ? getVenueRoleDisplayName(row.currentRole)
                              : '',
                          })),
                          leftVenues: (preview.rolePreview.left || []).map(
                            row => ({
                              title: row.title,
                              reason: row.reason,
                              currentRole: row.currentRole
                                ? getVenueRoleDisplayName(row.currentRole)
                                : '',
                            }),
                          ),
                        }
                      : {}),
                  }
                : payload?.removePerson,
              changePerson: payload?.changePerson
                ? {
                    ...payload.changePerson,
                    ...(preview.rolePreview && payload.remove !== true
                      ? {
                          venueTitles: (
                            preview.rolePreview.willChange || []
                          ).map(row => ({
                            title: row.title,
                            currentRole: row.currentRole
                              ? getVenueRoleDisplayName(row.currentRole)
                              : '',
                          })),
                          leftVenues: (preview.rolePreview.left || []).map(
                            row => ({
                              title: row.title,
                              reason: row.reason,
                              currentRole: row.currentRole
                                ? getVenueRoleDisplayName(row.currentRole)
                                : '',
                            }),
                          ),
                        }
                      : {}),
                  }
                : payload?.changePerson,
            })}
          </p>
          {allergenDetailLines(preview.allergenPreview, sourceTitle).map(
            line => (
              <p key={line} style={{ margin: '12px 0' }}>
                {line}
              </p>
            ),
          )}
          {revenueBlocked && preview.revenuePreview?.note && (
            <p style={{ margin: '12px 0' }}>{preview.revenuePreview.note}</p>
          )}
          {(preview.categoryNames || []).map(name => (
            <p key={name} style={{ margin: '12px 0' }}>
              {name}
            </p>
          ))}
          {showOnlyMissingChoice({
            action: preview.action,
            menusAlreadyThere: preview.menusAlreadyThere,
            revenueAlreadyThere,
          }) && (
            <label style={{ display: 'block', marginBottom: 12 }}>
              <input
                type='checkbox'
                style={checkboxStyle}
                checked={onlyMissingChecked}
                disabled={lockOnlyMissing}
                onChange={event => {
                  if (lockOnlyMissing) return
                  setOnlyMissing(event.target.checked)
                }}
              />
              Only venues that do not have this yet
            </label>
          )}
          {menuAlreadyLines(preview.menusAlreadyThere, onlyMissing).map(
            line => (
              <p key={line} style={{ margin: '12px 0' }}>
                {line}
              </p>
            ),
          )}
          {alreadyHaveLines({
            rows: preview.alreadyHave,
            counts: preview.recipeCounts,
            forceShare,
            onlyMissing,
          }).map(line => (
            <p key={line} style={{ margin: '12px 0' }}>
              {line}
            </p>
          ))}
          {restrictedApproverGaps(payload?.approvers).map(row => (
            <p key={row.supplierId || row.title} style={{ margin: '12px 0' }}>
              {row.title || 'This supplier'} has no approver. An order cannot be
              sent until someone can approve it.
            </p>
          ))}
          {restrictedApproverGaps(payload?.approvers).length > 0 && (
            <label style={{ display: 'block', marginBottom: 12 }}>
              <input
                type='checkbox'
                style={checkboxStyle}
                checked={allowWithoutApprover}
                onChange={event =>
                  setAllowWithoutApprover(event.target.checked)
                }
              />
              Continue without an approver
            </label>
          )}
          {shoppingListNeedsSupplier(preview.shoppingListSuppliers) && (
            <div style={{ margin: '12px 0' }}>
              <p style={{ margin: '0 0 8px' }}>
                {shoppingListSupplierSummary(preview.shoppingListSuppliers)}.
              </p>
              {shoppingListSupplierVenueGroups(
                preview.shoppingListSuppliers,
              ).map(group => (
                <details key={group.title} style={{ marginBottom: 8 }}>
                  <summary style={{ cursor: 'pointer' }}>
                    {group.title}
                    {` · ${group.supplierCount} supplier${
                      group.supplierCount === 1 ? '' : 's'
                    }`}
                  </summary>
                  {group.lines.map(line => (
                    <p key={line} style={{ margin: '8px 0 8px 1em' }}>
                      {line}
                    </p>
                  ))}
                  {group.turnOns.map(turnOn => (
                    <p key={turnOn.key} style={{ margin: '4px 0 8px 1em' }}>
                      <Link to={turnOn.href} target='_blank' rel='noreferrer'>
                        {turnOn.label}
                      </Link>
                    </p>
                  ))}
                </details>
              ))}
              {payload?.sourceVenueId &&
                shoppingListSuppliersToEnable(preview.shoppingListSuppliers)
                  .length > 0 && (
                  <Button
                    style={{ marginTop: 4 }}
                    loading={enablingSuppliers}
                    disabled={enablingSuppliers || confirming}
                    onClick={enableSuppliersOnTargets}
                  >
                    Enable these suppliers on targets
                  </Button>
                )}
            </div>
          )}
          {shoppingListNeedsSupplier(preview.shoppingListSuppliers) && (
            <label style={{ display: 'block', marginBottom: 12 }}>
              <input
                type='checkbox'
                style={checkboxStyle}
                checked={allowPartialList}
                onChange={event => setAllowPartialList(event.target.checked)}
              />
              Share only the lines that can land
            </label>
          )}
          {showCopyWithoutDishes({
            action: preview.action,
            partial: partialDishes,
          }) && (
            <label style={{ display: 'block', marginBottom: 12 }}>
              <input
                type='checkbox'
                style={checkboxStyle}
                checked={allowWithoutDishes}
                onChange={event => setAllowWithoutDishes(event.target.checked)}
              />
              Copy the menu without the dishes that cannot land
            </label>
          )}
          {missingRecipeLines(preview.missingRecipes).map((line, index) => (
            <p key={`${line}-${index}`} style={{ margin: '12px 0' }}>
              {line}
            </p>
          ))}
          {(showMenuRecipeCounts({
            partial: partialDishes,
            allowWithoutDishes,
          })
            ? menuRecipeCountLines(
                preview.recipeCounts,
                preview.excludesSource === true,
                noDishes,
                preview?.action === 'copy_menu' &&
                  (preview.ingredientGaps || []).length > 0,
              )
            : []
          ).map(line => (
            <p key={line}>{line}</p>
          ))}
          {ingredientGapLines(
            preview.ingredientGaps,
            forceShare,
            preview.excludesSource === true,
          ).map(line => (
            <p key={line}>{line}</p>
          ))}
          {recipeGapVenueLinks(preview.ingredientGaps).map(link => (
            <p key={link.key} style={{ margin: '8px 0' }}>
              <Link to={link.href} target='_blank' rel='noreferrer'>
                {link.label}
              </Link>
              {' · '}
              <Link to={link.suppliersHref} target='_blank' rel='noreferrer'>
                {link.suppliersLabel}
              </Link>
            </p>
          ))}
          {action === 'share_recipes' && (offerForceShare || forceShare) && (
            <label>
              <input
                type='checkbox'
                style={checkboxStyle}
                checked={forceShare}
                onChange={event => setForceShare(event.target.checked)}
              />
              Force Share Anyway
            </label>
          )}
          {(preview.localChanges || []).length > 0 && (
            <div>
              <p>
                {allergenEditedLine({
                  action: preview.action,
                  localChanges: preview.localChanges,
                }) ||
                  'These venues have local changes. They are skipped unless you replace them anyway.'}
              </p>
              <label>
                <input
                  type='checkbox'
                  style={checkboxStyle}
                  checked={replaceAll}
                  onChange={event => setReplaceAll(event.target.checked)}
                />
                Replace all of these anyway
              </label>
              {(preview.localChanges || []).map(venue => (
                <label key={venue.venue}>
                  <input
                    type='checkbox'
                    style={checkboxStyle}
                    disabled={replaceAll}
                    checked={
                      replaceAll || replaceIds.includes(String(venue.venue))
                    }
                    onChange={() =>
                      setReplaceIds(current =>
                        current.includes(String(venue.venue))
                          ? current.filter(id => id !== String(venue.venue))
                          : [...current, String(venue.venue)],
                      )
                    }
                  />
                  {venue.title || venue.venue}
                </label>
              ))}
            </div>
          )}
        </>
      ) : (
        <p>Checking which venues will change.</p>
      )}
    </Modal>
  )
}

export default BulkActionConfirm
